
import React, { useState, useEffect } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Loader2, Brain } from "lucide-react";
import { toast } from "sonner";
import { Account, AnalysisResult, FinancialData, KpiData, FrameworkData } from "./advisor/types";
import { fetchFinancialData, fetchKpiData, fetchFrameworkData, generateInsightsService } from "./advisor/dataService";
import LoadingState from "./advisor/LoadingState";
import EmptyState from "./advisor/EmptyState";
import ExecutiveSummary from "./advisor/ExecutiveSummary";
import AnalysisTabs from "./advisor/AnalysisTabs";

const AISalesAdvisorTab = ({
  selectedAccount
}: {
  selectedAccount: Account | null;
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [financialData, setFinancialData] = useState<FinancialData | null>(null);
  const [kpiData, setKpiData] = useState<KpiData | null>(null);
  const [frameworkData, setFrameworkData] = useState<FrameworkData | null>(null);
  const [analysisResult, setAnalysisResult] = useState<AnalysisResult | null>(null);
  const [activeInsightTab, setActiveInsightTab] = useState("focus");

  useEffect(() => {
    if (selectedAccount?.company_id) {
      setAnalysisResult(null);
    }
  }, [selectedAccount?.company_id]);

  const generateInsights = async () => {
    if (!selectedAccount?.company_id) {
      toast.error("Please select an account first");
      return;
    }
    
    try {
      setIsLoading(true);
      setAnalysisResult(null);
      
      // Fetch all required data
      const [financial, kpiResponse, framework] = await Promise.all([
        fetchFinancialData(selectedAccount.company_id),
        fetchKpiData(selectedAccount.company_id),
        fetchFrameworkData(selectedAccount.company_id)
      ]);
      
      if (financial) setFinancialData(financial);
      if (kpiResponse.data) setKpiData(kpiResponse.data);
      if (framework) setFrameworkData(framework);
      
      if (!financial && !kpiResponse.data && !framework) {
        toast.error("No data available for analysis. Please ensure you've filled in information in other tabs.");
        setIsLoading(false);
        return;
      }
      
      // Call the edge function to analyze data
      const analysis = await generateInsightsService(selectedAccount, financial, kpiResponse.data, framework);
      setAnalysisResult(analysis);
      toast.success("AI analysis complete");
    } catch (error) {
      console.error("Error generating insights:", error);
      toast.error(`Error: ${(error as Error).message}`);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Card className="bg-secondary/50 backdrop-blur-lg border-2 border-primary rounded-lg shadow-lg shadow-purple-500/10 my-[12px]">
      <CardContent className="mt-4">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h2 className="text-2xl font-bold bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 text-transparent bg-clip-text">
              AI Sales Advisor
            </h2>
            <p className="text-white text-[13px]">
              Strategic recommendations based on customer data analysis
              <p className="text-[12px] text-white">(Note: AI hallucination may occur. Validation of generated information is essential.)</p>
            </p>
          </div>
          
          <Button 
            onClick={generateInsights} 
            disabled={isLoading || !selectedAccount}
            className="bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white"
          >
            {isLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Analyzing Data...
              </>
            ) : (
              <>
                <Brain className="mr-2 h-4 w-4" />
                Generate Strategic Insights
              </>
            )}
          </Button>
        </div>
        
        {isLoading ? (
          <LoadingState />
        ) : !analysisResult ? (
          <EmptyState selectedAccount={selectedAccount} />
        ) : (
          <div className="space-y-6">
            <ExecutiveSummary summary={analysisResult.summary} />
            <AnalysisTabs 
              analysisResult={analysisResult}
              activeTab={activeInsightTab}
              onTabChange={setActiveInsightTab}
            />
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default AISalesAdvisorTab;
